import { KeyboardEvent, useEffect, useRef, useState } from 'react';

import { isNullOrUndefined } from '@vy/utils';

type ClickToEditValueType = 'String' | 'Number';

type UseClickToEditCustomFieldValueParameters = {
  value: unknown;
  valueType: ClickToEditValueType;
  onCommit: (next: string | number | null) => void;
  onBlur: () => void;
};

const formatDraft = (value: unknown): string => {
  if (isNullOrUndefined(value)) {
    return '';
  }
  return `${value}`;
};

export const useClickToEditCustomFieldValue = ({
  value,
  valueType,
  onCommit,
  onBlur,
}: UseClickToEditCustomFieldValueParameters) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<string>(() => formatDraft(value));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const commitDraft = () => {
    if (valueType === 'Number') {
      if (draft.trim() === '') {
        onCommit(null);
      } else {
        const parsed = Number(draft);
        if (Number.isNaN(parsed)) {
          return;
        }
        onCommit(parsed);
      }
    } else {
      onCommit(draft);
    }
    setIsEditing(false);
    onBlur();
  };

  const cancelEdit = () => {
    setDraft(formatDraft(value));
    setIsEditing(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitDraft();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelEdit();
    }
  };

  const enterEditMode = () => {
    setDraft(formatDraft(value));
    setIsEditing(true);
  };

  return {
    isEditing,
    draft,
    setDraft,
    inputRef,
    enterEditMode,
    commitDraft,
    cancelEdit,
    handleKeyDown,
  };
};
